import type { TtqQuestionChainVariantId } from './ttqBehaviorCatalog';
import { TTQ_QUESTION_CHAIN_VARIANTS } from './ttqBehaviorCatalog';
import { repairPairedCornerQuotes } from '../../utils/repairPairedCornerQuotes';

export type ExplosionSegment = {
  id: string;
  text: string;
  variantIds: TtqQuestionChainVariantId[];
  /** 高亮段可导向一题多题变式 */
  suggestNewQuestion: boolean;
};

export type VariantPreview = {
  variantId: TtqQuestionChainVariantId;
  label: string;
  hint: string;
};

export type QuestionExplosionModel = {
  fullText: string;
  segments: ExplosionSegment[];
  /** 无法锚定到文本段的 GO-ON 变式 */
  unmappedVariants: VariantPreview[];
};

const VARIANT_HINT: Record<TtqQuestionChainVariantId, string> = {
  question_chain: '在此片段上追加递进小问，形成同题干题链。',
  condition_generalize: '将具体数值代数化，突出通性结构。',
  condition_specialize: '代入具体数值，降维巩固解法。',
  conclusion_progression: '把当前结论作为下一问条件，递进加强或减弱。',
  inverse_swap: '交换条件与结论角色，训练逆向思维。',
  context_transfer: '保留模型，更换应用情境背景。',
  element_weak_strong: '弱化/强化/增删该条件或结论要素。',
  depth_extend: '向更深或更综合方向延伸探究。',
  error_boundary: '围绕该片段设置易错辨析或边界陷阱。',
  custom: '按自定义意图对该片段做变式。',
};

type Rule = { pattern: RegExp; variants: TtqQuestionChainVariantId[] };

const SEGMENT_RULES: Rule[] = [
  { pattern: /\d+(?:\.\d+)?/g, variants: ['condition_specialize', 'condition_generalize'] },
  { pattern: /[a-zA-Z](?:_[a-zA-Z0-9]+)?/g, variants: ['condition_generalize', 'condition_specialize'] },
  { pattern: /已知|设|若|当/g, variants: ['element_weak_strong', 'condition_generalize'] },
  { pattern: /求|证|证明|求证/g, variants: ['conclusion_progression', 'inverse_swap'] },
  { pattern: /应用|实际|行程|利润|工程|面积|速度/g, variants: ['context_transfer'] },
  { pattern: /分类|讨论|增根|定义域|取值范围/g, variants: ['error_boundary'] },
  { pattern: /又|再|进一步|递推|推广/g, variants: ['question_chain', 'depth_extend'] },
];

/** 整题级、难锚定文本的变式 */
const GLOBAL_VARIANTS: TtqQuestionChainVariantId[] = [
  'depth_extend',
  'error_boundary',
  'context_transfer',
  'custom',
];

/** 收集 $...$ / $$...$$ / \(...\) / \[...\] 区间，避免爆炸分段切断公式 */
function collectMathRanges(text: string): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] === '\\' && text[i + 1] === '$') {
      i += 2;
      continue;
    }

    if (text.startsWith('$$', i)) {
      const close = text.indexOf('$$', i + 2);
      if (close < 0) {
        ranges.push({ start: i, end: text.length });
        break;
      }
      ranges.push({ start: i, end: close + 2 });
      i = close + 2;
      continue;
    }

    if (text[i] === '$') {
      let close = -1;
      let j = i + 1;
      while (j < text.length) {
        if (text[j] === '\\' && text[j + 1] === '$') {
          j += 2;
          continue;
        }
        if (text[j] === '$') {
          close = j;
          break;
        }
        j += 1;
      }
      if (close < 0) {
        ranges.push({ start: i, end: text.length });
        break;
      }
      ranges.push({ start: i, end: close + 1 });
      i = close + 1;
      continue;
    }

    if (text.startsWith('\\(', i)) {
      const close = text.indexOf('\\)', i + 2);
      if (close < 0) {
        ranges.push({ start: i, end: text.length });
        break;
      }
      ranges.push({ start: i, end: close + 2 });
      i = close + 2;
      continue;
    }

    if (text.startsWith('\\[', i)) {
      const close = text.indexOf('\\]', i + 2);
      if (close < 0) {
        ranges.push({ start: i, end: text.length });
        break;
      }
      ranges.push({ start: i, end: close + 2 });
      i = close + 2;
      continue;
    }

    const beginMatch = text.slice(i).match(/^\\begin\{([a-zA-Z*]+)\}/);
    if (beginMatch) {
      const env = beginMatch[1];
      const endToken = `\\end{${env}}`;
      const close = text.indexOf(endToken, i + beginMatch[0].length);
      if (close < 0) {
        ranges.push({ start: i, end: text.length });
        break;
      }
      ranges.push({ start: i, end: close + endToken.length });
      i = close + endToken.length;
      continue;
    }

    i += 1;
  }
  return ranges;
}

function isInsideMath(index: number, ranges: Array<{ start: number; end: number }>): boolean {
  return ranges.some((range) => index >= range.start && index < range.end);
}

const LIST_COMMA_CHARS = '，,';

function isListCommaChar(ch: string): boolean {
  return LIST_COMMA_CHARS.includes(ch);
}

function isAsciiIdentChar(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}

function isHanChar(ch: string): boolean {
  return /[\u4e00-\u9fff]/.test(ch);
}

/** 逗号列举中的原子词项：单字符 / 数字 / 单词 / 标识符 */
function isAtomicListToken(token: string): boolean {
  if (!token) return false;
  if ([...token].length === 1) return true;
  if (/^\d+$/.test(token)) return true;
  if (/^[A-Za-z0-9_]+$/.test(token)) return true;
  return false;
}

function isHanListToken(token: string): boolean {
  return [...token].length === 1 && isHanChar(token);
}

function isAsciiListToken(token: string): boolean {
  return isAtomicListToken(token) && ![...token].some((ch) => isHanChar(ch));
}

/**
 * 读取逗号左侧紧邻的原子词项；若落在更长汉字词或标识符中间则视为非原子。
 * 用于识别 a,b,c / 1,23 / 甲,乙 / foo,bar 这类列举，避免误切。
 */
function readAtomicTokenBefore(
  text: string,
  commaIndex: number,
  ranges: Array<{ start: number; end: number }>,
): string | null {
  let end = commaIndex - 1;
  while (end >= 0 && /\s/.test(text[end])) {
    if (isInsideMath(end, ranges)) return null;
    end -= 1;
  }
  if (end < 0 || isInsideMath(end, ranges) || isListCommaChar(text[end])) return null;

  let start = end;
  const ch = text[end];
  // 数字与标识符统一按 [A-Za-z0-9_]+ 回退，避免 x1 / y2 被拆成纯数字后缀
  if (isAsciiIdentChar(ch)) {
    while (start > 0 && isAsciiIdentChar(text[start - 1]) && !isInsideMath(start - 1, ranges)) {
      start -= 1;
    }
  } else {
    start = end;
  }

  const token = text.slice(start, end + 1);
  if (!isAtomicListToken(token)) return null;

  if (start > 0 && !isInsideMath(start - 1, ranges)) {
    const prev = text[start - 1];
    if (isHanChar(token[0]) && isHanChar(prev)) return null;
    if (isAsciiIdentChar(token[0]) && isAsciiIdentChar(prev)) return null;
  }
  return token;
}

function readAtomicTokenAfter(
  text: string,
  commaIndex: number,
  ranges: Array<{ start: number; end: number }>,
): string | null {
  let start = commaIndex + 1;
  while (start < text.length && /\s/.test(text[start])) {
    if (isInsideMath(start, ranges)) return null;
    start += 1;
  }
  if (start >= text.length || isInsideMath(start, ranges) || isListCommaChar(text[start])) return null;

  let end = start;
  const ch = text[start];
  if (isAsciiIdentChar(ch)) {
    while (end + 1 < text.length && isAsciiIdentChar(text[end + 1]) && !isInsideMath(end + 1, ranges)) {
      end += 1;
    }
  } else {
    end = start;
  }

  const token = text.slice(start, end + 1);
  if (!isAtomicListToken(token)) return null;

  if (end + 1 < text.length && !isInsideMath(end + 1, ranges)) {
    const next = text[end + 1];
    if (isHanChar(token[token.length - 1]) && isHanChar(next)) return null;
    if (isAsciiIdentChar(token[token.length - 1]) && isAsciiIdentChar(next)) return null;
  }
  return token;
}

/** 逗号两侧都是同类原子词项时视为连续列举，不在此处切断 */
function shouldKeepCommaContinuous(
  text: string,
  commaIndex: number,
  ranges: Array<{ start: number; end: number }>,
): boolean {
  if (!isListCommaChar(text[commaIndex])) return false;
  const left = readAtomicTokenBefore(text, commaIndex, ranges);
  const right = readAtomicTokenAfter(text, commaIndex, ranges);
  if (!left || !right) return false;
  // 甲,乙 同类汉字；a,b / 1,23 / x1,y2 同类 ASCII；避免「1，求」被误粘
  if (isHanListToken(left) && isHanListToken(right)) return true;
  if (isAsciiListToken(left) && isAsciiListToken(right)) return true;
  return false;
}

function splitByPunctuationOutsideMath(
  text: string,
  punctuation: string,
  ranges: Array<{ start: number; end: number }>,
): string[] {
  const breakEnds: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    if (isInsideMath(i, ranges)) continue;
    if (!punctuation.includes(text[i])) continue;
    if (shouldKeepCommaContinuous(text, i, ranges)) continue;
    breakEnds.push(i + 1);
  }
  if (breakEnds.length === 0) return [];

  const parts: string[] = [];
  let start = 0;
  for (const end of breakEnds) {
    const chunk = text.slice(start, end).trim();
    if (chunk) parts.push(chunk);
    start = end;
    while (start < text.length && /\s/.test(text[start])) start += 1;
  }
  const tail = text.slice(start).trim();
  if (tail) parts.push(tail);
  return parts;
}

function splitIntoClauses(text: string): string[] {
  const ranges = collectMathRanges(text);
  // 切分：中英文逗号与 。；？！；顿号（、）永不切断，留在段内视作整体
  const parts = splitByPunctuationOutsideMath(text, `${LIST_COMMA_CHARS}。；;？?！!`, ranges);
  if (parts.length > 1) return parts;
  const trimmed = text.trim();
  return trimmed ? [trimmed] : [];
}

function mapVariantsForChunk(chunk: string): TtqQuestionChainVariantId[] {
  const set = new Set<TtqQuestionChainVariantId>();
  for (const rule of SEGMENT_RULES) {
    rule.pattern.lastIndex = 0;
    if (rule.pattern.test(chunk)) {
      rule.variants.forEach((v) => set.add(v));
    }
  }
  if (set.size === 0 && chunk.length > 4) {
    set.add('element_weak_strong');
    set.add('question_chain');
  }
  return [...set];
}

export function buildQuestionExplosion(questionText: string): QuestionExplosionModel {
  const fullText = repairPairedCornerQuotes(questionText.trim()) || '（暂无题干）';
  const chunks = splitIntoClauses(fullText);
  const mappedVariantSet = new Set<TtqQuestionChainVariantId>();

  const segments: ExplosionSegment[] = chunks.map((text, index) => {
    const variantIds = mapVariantsForChunk(text);
    variantIds.forEach((v) => mappedVariantSet.add(v));
    return {
      id: `seg-${index}`,
      text,
      variantIds,
      suggestNewQuestion: variantIds.length > 0,
    };
  });

  const unmappedIds = [
    ...GLOBAL_VARIANTS.filter((v) => !mappedVariantSet.has(v)),
    ...TTQ_QUESTION_CHAIN_VARIANTS.map((v) => v.id).filter(
      (id) => !mappedVariantSet.has(id) && !GLOBAL_VARIANTS.includes(id),
    ),
  ].filter((id, i, arr) => arr.indexOf(id) === i);

  const unmappedVariants: VariantPreview[] = unmappedIds.map((variantId) => {
    const meta = TTQ_QUESTION_CHAIN_VARIANTS.find((v) => v.id === variantId);
    return {
      variantId,
      label: meta?.label || variantId,
      hint: VARIANT_HINT[variantId] || meta?.description || '',
    };
  });

  return { fullText, segments, unmappedVariants };
}

/** 按题干顺序拼接已选片段文本 */
export function buildAnchorTextFromSegments(
  segments: ExplosionSegment[],
  selectedIds: string[],
): string {
  const idSet = new Set(selectedIds);
  return segments.filter((s) => idSet.has(s.id)).map((s) => s.text).join('');
}

/** 已选片段可用的变式方向（并集） */
export function variantIdsForSelectedSegments(
  segments: ExplosionSegment[],
  selectedIds: string[],
): TtqQuestionChainVariantId[] {
  const selected = segments.filter((s) => selectedIds.includes(s.id));
  if (selected.length === 0) return [];
  const union = new Set<TtqQuestionChainVariantId>();
  selected.forEach((s) => s.variantIds.forEach((v) => union.add(v)));
  return [...union];
}

export function buildVariantPreview(variantId: TtqQuestionChainVariantId, anchorText?: string): string {
  const label = TTQ_QUESTION_CHAIN_VARIANTS.find((v) => v.id === variantId)?.label || variantId;
  const anchor = anchorText ? `「${anchorText.slice(0, 40)}${anchorText.length > 40 ? '…' : ''}」` : '本题';
  return `对 ${anchor} 做【${label}】：${VARIANT_HINT[variantId]}`;
}
